import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Minus, Plus, ShoppingCart, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';
import { toast } from '../hooks/use-toast';
import { HoverZoomImage } from '../components/HoverZoomImage';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [displaySettings, setDisplaySettings] = useState({ show_prices: true, show_stock: true });
  const { addToCart, customPrices } = useCart();

  const contractPrice = product && customPrices && customPrices[product.id] !== undefined ? Number(customPrices[product.id]) : null;

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    setError(false);
    setQuantity(1);
    try {
      const [productRes, settingsRes] = await Promise.all([
        axios.get(`${API}/products/${id}`),
        axios.get(`${API}/settings`)
      ]);
      const prod = productRes.data;
      setProduct(prod);
      setDisplaySettings(settingsRes.data);

      // Fetch related products from same category
      const relatedRes = await axios.get(`${API}/products`, {
        params: { category: prod.category, limit: 5 }
      });
      setRelatedProducts(relatedRes.data.filter(p => p.id !== prod.id).slice(0, 4));
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast({
      title: "Added to cart",
      description: `${quantity} x ${product.name} added to your cart.`,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="product-detail-loading">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="product-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Product not found</h2>
          <p className="text-slate-500 mb-6">The product you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/products')} data-testid="back-to-products-btn">Back to Products</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="product-detail-page">
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Product Image */}
          <div>
            <Card className="overflow-hidden border-2">
              <CardContent className="p-0">
                <HoverZoomImage
                  src={product.image}
                  alt={product.name}
                  className="w-full"
                  testId="product-image"
                />
              </CardContent>
            </Card>
            <p className="mt-2 text-[11px] text-slate-500 text-center hidden md:block">
              Hover over the image to zoom in.
            </p>
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4" data-testid="product-name">{product.name}</h1>
            <div className="flex items-center gap-4 mb-6">
              {displaySettings.show_prices ? (
                contractPrice !== null ? (
                  <div className="flex items-baseline gap-3" data-testid="product-price">
                    <span className="text-3xl sm:text-4xl font-bold text-emerald-600">${contractPrice.toFixed(2)}</span>
                    <span className="text-base text-slate-400 line-through">${(product.retail_price ?? product.price)}</span>
                    <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Contract</span>
                  </div>
                ) : (
                  <span className="text-3xl sm:text-4xl font-bold text-purple-600" data-testid="product-price">${(product.retail_price ?? product.price)}</span>
                )
              ) : (
                <span className="text-base text-slate-500 italic" data-testid="product-price-hidden">Contact for pricing</span>
              )}
              {displaySettings.show_stock ? (
                <span className={`px-4 py-1 rounded-full text-sm font-medium ${
                  product.stock > 0
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`} data-testid="product-stock">
                  {product.stock > 0 ? `${product.stock} in stock` : 'Out of Stock'}
                </span>
              ) : (
                <span className={`px-4 py-1 rounded-full text-sm font-medium ${
                  product.stock > 0
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`} data-testid="product-stock">
                  {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                </span>
              )}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <h3 className="font-semibold text-lg mb-2 text-slate-800">Description</h3>
              <p className="text-slate-600 leading-relaxed" data-testid="product-description">{product.description}</p>
            </div>

            {/* Quantity Selector */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <label className="font-semibold text-slate-800 mb-3 block">Quantity</label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <div className="flex items-center border-2 border-slate-300 rounded-lg justify-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    data-testid="quantity-decrease"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="px-6 font-semibold text-lg" data-testid="quantity-value">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    disabled={quantity >= product.stock}
                    data-testid="quantity-increase"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  size="lg"
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={handleAddToCart}
                  disabled={product.stock === 0}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Add to Cart
                </Button>
              </div>
            </div>

            {/* Additional Info */}
            <Card className="border-2">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Category:</span>
                    <span className="font-semibold text-slate-800 capitalize">
                      {product.category.replace('-', ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Availability:</span>
                    <span className="font-semibold text-slate-800">
                      {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-6">Related Products</h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {relatedProducts.map(relatedProduct => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="group"
                  data-testid={`related-product-${relatedProduct.id}`}
                >
                  <Card className="hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-purple-500 h-full">
                    <CardContent className="p-2 sm:p-4">
                      <div className="aspect-square bg-slate-100 rounded-lg mb-2 sm:mb-4 overflow-hidden">
                        <img
                          src={relatedProduct.image}
                          alt={relatedProduct.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <h3 className="font-semibold text-xs sm:text-base text-slate-800 mb-1 sm:mb-2 line-clamp-2 group-hover:text-purple-600 transition-colors">
                        {relatedProduct.name}
                      </h3>
                      {displaySettings.show_prices && (
                        <span className="text-base sm:text-xl font-bold text-purple-600">${relatedProduct.price}</span>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
